import type { I18n } from '@lingui/core';
import { keepPreviousData, useQuery } from '@tanstack/react-query';

import { ApiError } from '@/shared/http';

/** Uma opção de entidade: valor (id), rótulo e linhas extras ("CPF/CNPJ: …", "Parceiro: …"). */
export interface EntityOption {
  readonly value: string;
  readonly label: string;
  readonly description?: string;
}

/**
 * De onde vêm as opções de um seletor de entidade (P06): cada módulo declara a sua (pessoa, banco,
 * fundo…) sobre o próprio fio; o campo não conhece o endpoint. A busca é local ao campo, nunca
 * escreve no filtro da lista (06 §6).
 */
export interface EntitySource<T = unknown> {
  /** Prefixo da chave de cache (`['fund', 'person']`). */
  readonly key: readonly unknown[];
  /** Entra na chave: o que muda o resultado fora da busca (o correspondente, U37-3). */
  readonly scope?: () => unknown;
  readonly search: (query: string, signal: AbortSignal) => Promise<readonly T[]>;
  /** Rótulo do valor salvo quando ele não está na última busca (URL restaurada, registro aberto). */
  readonly byId?: (id: string, signal: AbortSignal) => Promise<T | undefined>;
  readonly toOption: (item: T, i18n: I18n) => EntityOption;
  /** Letras antes de buscar (padrão 0: abre já com a primeira página, como o legado). */
  readonly minQuery?: number;
}

/** Declara a fonte com o tipo do item inferido. */
export function defineEntitySource<T>(source: EntitySource<T>): EntitySource<T> {
  return source;
}

export interface LocalEntitySourceOptions<T> {
  readonly key: readonly unknown[];
  /** Muda o resultado fora da busca (o correspondente): outro valor recarrega a lista. */
  readonly scope?: () => unknown;
  /** Carrega a lista inteira uma vez; o filtro por texto é local. */
  readonly load: (signal: AbortSignal) => Promise<readonly T[]>;
  /** Só estes itens entram (ex.: COSIF com conta de débito vinculada). */
  readonly include?: (item: T) => boolean;
  /** Texto em que a busca procura (sem caixa e sem acento, como o autocomplete do legado). */
  readonly text: (item: T) => string;
  readonly toOption: (item: T, i18n: I18n) => EntityOption;
  /** Quanto a lista carregada vale, em ms; padrão 60 s (o legado recarregava ao montar). */
  readonly ttlMs?: number;
}

const normalize = (value: string) =>
  value
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase();

// eslint-disable-next-line lingui/no-unlocalized-strings -- nome técnico do erro de cancelamento, não texto de UI
const abortError = (reason: unknown) => (reason instanceof Error ? reason : new DOMException('Aborted', 'AbortError'));

function raceAbort<R>(promise: Promise<R>, signal: AbortSignal): Promise<R> {
  if (signal.aborted) return Promise.reject(abortError(signal.reason));
  const aborted = new Promise<never>((_, reject) => {
    signal.addEventListener('abort', () => reject(abortError(signal.reason)), { once: true });
  });
  return Promise.race([promise, aborted]);
}

/**
 * Fonte sobre uma lista carregada inteira (o autocomplete local do legado): uma chamada por `ttlMs`
 * e por `scope`, compartilhada entre as buscas; cada tecla só filtra. Falha não fica em cache, e
 * cancelar uma busca não cancela a carga das outras.
 */
export function defineLocalEntitySource<T>(options: LocalEntitySourceOptions<T>): EntitySource<T> & { readonly reset: () => void } {
  const ttl = options.ttlMs ?? 60_000;
  let cache: { readonly scope: unknown; readonly at: number; readonly promise: Promise<readonly T[]> } | undefined;
  const loadAll = () => {
    const scope = options.scope?.();
    const now = performance.now();
    if (!cache || cache.scope !== scope || now - cache.at > ttl) {
      const promise = options.load(new AbortController().signal);
      const entry = { scope, at: now, promise };
      cache = entry;
      promise.catch(() => {
        if (cache === entry) cache = undefined;
      });
    }
    return cache.promise;
  };
  return {
    key: options.key,
    ...(options.scope ? { scope: options.scope } : {}),
    search: async (query, signal) => {
      const all = await raceAbort(loadAll(), signal);
      const needle = normalize(query.trim());
      return all.filter((item) => (options.include?.(item) ?? true) && (needle === '' || normalize(options.text(item)).includes(needle)));
    },
    toOption: options.toOption,
    reset: () => {
      cache = undefined;
    },
  };
}

const wrap = async <R>(run: () => Promise<R>) => {
  try {
    return await run();
  } catch (error) {
    throw ApiError.from(error);
  }
};

/** Busca remota da fonte, com a lista anterior na tela enquanto a nova chega. */
export function useEntitySearch<T>(source: EntitySource<T>, query: string, enabled: boolean) {
  const trimmed = query.trim();
  const ready = enabled && trimmed.length >= (source.minQuery ?? 0);
  return useQuery<readonly T[], ApiError>({
    queryKey: [...source.key, 'search', source.scope?.() ?? null, trimmed],
    queryFn: ({ signal }) => wrap(() => source.search(trimmed, signal)),
    enabled: ready,
    placeholderData: keepPreviousData,
    staleTime: 30_000,
  });
}

/** O item de um id, pela `byId` da fonte (desligado sem `byId` ou sem id). */
export function useEntityById<T>(source: EntitySource<T>, id: string | null | undefined, enabled = true) {
  const byId = source.byId;
  return useQuery<T | undefined, ApiError>({
    queryKey: [...source.key, 'byId', source.scope?.() ?? null, id ?? null],
    queryFn: ({ signal }) => wrap(() => (byId && id ? byId(id, signal) : Promise.resolve(undefined))),
    enabled: enabled && byId !== undefined && typeof id === 'string' && id !== '',
    staleTime: 5 * 60_000,
  });
}
